import {
    isCategoricalGroupByOption,
    isDateGroupByGrain,
    isDateGroupByOption,
    isNumericRangeGroupByOption,
    isRecord,
    isStrictlyIncreasing,
    toOptionalString,
    type GroupByDraftConfiguration,
    type GroupByOptionValue,
} from "./GroupByTypes";
import type { SignalsParsedVizOptions } from "../SignalsModels";

/**
 * The editing half of the group-by contract.
 *
 * A chart's grouping has two readers with opposite needs. At request time it must be complete or
 * absent -- there is no useful middle ground, and `normalizeGroupByConfiguration` enforces that by
 * returning `undefined` for anything partial. In the configuration drawer, partial is the normal
 * state: the user picks a dimension, and only then picks values for it.
 *
 * A *draft* is that second reading. Same shape, looser rules:
 *
 * | | `normalizeGroupByConfiguration` | `readGroupByDraft` |
 * | --- | --- | --- |
 * | Categorical with no values | `undefined` | draft with `valueIds: []` |
 * | Numeric range with no cut points | `undefined` | draft with `cutPoints: []` |
 * | Numeric range with an untyped boundary | `undefined` | draft with `null` in `cutPoints` |
 * | Date with an unrecognized grain | `undefined` | draft defaulted to `auto` |
 * | Unknown option | `undefined` | `undefined` |
 * | Kind that disagrees with its option | `undefined` | draft for the *option's* kind |
 *
 * That last row is the one place the two readers genuinely differ, and it is deliberate: the strict
 * reader dispatches on the stored `kind` and rejects a mismatch as malformed, while the draft
 * dispatches on `option` -- the thing the user actually picked -- so the drawer can still open on a
 * chart whose stored `kind` is wrong. Nothing the app writes can produce a mismatch, since
 * `writeGroupByDraft` always writes an agreeing pair.
 *
 * Drafts are never persisted as a distinct shape: `writeGroupByDraft` puts one back into
 * vizOptions, and `validateGroupByDraft` is what stops an incomplete one from being saved. So a
 * saved chart always satisfies the strict reader.
 */
export type GroupByDraft = GroupByDraftConfiguration;

/** The numeric-range branch of a draft, for the range editor and the helpers below. */
export type NumericRangeDraft = Extract<GroupByDraft, { kind: "numericRange" }>;

export type GroupByDraftValidation = {
    /** False blocks Save. */
    canSave: boolean;
    /** Shown next to the editor. Absent when the incomplete state speaks for itself. */
    message?: string;
};

/**
 * How many values the drawer lets a user group by. A readability limit rather than a contract one:
 * the backend does not cap value ids, so this gates the pickers and Save, and nothing rejects a
 * larger list that reaches the API another way.
 */
export const MAX_GROUP_BY_SELECTIONS = 50;

export const groupBySelectionLimitMessage = `Only ${MAX_GROUP_BY_SELECTIONS} values may be selected for grouping.`;

const toStringArray = (value: unknown): string[] =>
    Array.isArray(value)
        ? value.filter((item): item is string => typeof item === "string")
        : [];

/**
 * Cut points as the editor holds them: a row the user has not filled in reads as `null` rather than
 * being dropped. Dropping it would silently delete the row on the next render, which is the whole
 * reason the draft shape allows a hole. Anything that is neither a finite number nor absent -- a
 * string, a NaN -- is read as a pending row too, since that is the state the user has to resolve.
 */
const toCutPointArray = (value: unknown): (number | null)[] =>
    Array.isArray(value)
        ? value.map((item) =>
              typeof item === "number" && Number.isFinite(item) ? item : null,
          )
        : [];

/**
 * Rebuilds a draft from persisted canonical configuration. The option decides the kind and which
 * fields are read, so a stored `kind` that disagrees with its option is ignored rather than
 * trusted. Fields that are missing or unusable fall back to an empty editing state instead of
 * failing the whole read -- the user is about to edit them anyway.
 */
const readCanonicalDraft = (value: unknown): GroupByDraft | undefined => {
    if (!isRecord(value)) return undefined;

    if (isCategoricalGroupByOption(value.option)) {
        return {
            kind: "categorical",
            option: value.option,
            valueIds: toStringArray(value.valueIds),
            llmClassifierId: toOptionalString(value.llmClassifierId),
            extendedMetadataKey: toOptionalString(value.extendedMetadataKey),
            questionId: toOptionalString(value.questionId),
            licensedModuleId: toOptionalString(value.licensedModuleId),
        };
    }

    if (isDateGroupByOption(value.option)) {
        return {
            kind: "date",
            option: value.option,
            grain: isDateGroupByGrain(value.grain) ? value.grain : "auto",
        };
    }

    if (isNumericRangeGroupByOption(value.option)) {
        return {
            kind: "numericRange",
            option: value.option,
            cutPoints: toCutPointArray(value.cutPoints),
            moduleId: toOptionalString(value.moduleId),
        };
    }

    return undefined;
};

/**
 * Reads the draft the drawer should edit. Canonical `groupBy` wins; the legacy flat fields are read
 * only for charts saved before the canonical shape existed.
 */
export const readGroupByDraft = (
    vizOptions: SignalsParsedVizOptions | null | undefined,
): GroupByDraft | undefined => {
    if (!vizOptions) return undefined;

    // Present but unreadable canonical configuration means the chart is ungrouped, not that the
    // legacy fields should speak for it -- they may describe a grouping the user has since
    // replaced, and the writer keeps them in sync only for categorical grouping.
    if (vizOptions.groupBy !== undefined) {
        return readCanonicalDraft(vizOptions.groupBy);
    }

    return isCategoricalGroupByOption(vizOptions.groupByOption)
        ? {
              kind: "categorical",
              option: vizOptions.groupByOption,
              valueIds: toStringArray(vizOptions.groupByIds),
              llmClassifierId: toOptionalString(
                  vizOptions.groupByLlmClassifierId,
              ),
              extendedMetadataKey: toOptionalString(
                  vizOptions.groupByExtendedMetadataKey,
              ),
          }
        : undefined;
};

/**
 * Writes the draft back into vizOptions -- the only place that decides how grouping is stored.
 *
 * Canonical `groupBy` is the whole record. The legacy flat fields are cleared rather than mirrored:
 * every reader now understands canonical grouping, and leaving a copy behind would let the two
 * disagree. Clearing them also means re-saving a chart migrates its stored shape.
 *
 * Passing `undefined` ungroups the chart.
 */
export const writeGroupByDraft = (
    vizOptions: SignalsParsedVizOptions,
    draft: GroupByDraft | undefined,
): SignalsParsedVizOptions => ({
    ...vizOptions,
    groupBy: draft,
    groupByOption: undefined,
    groupByIds: undefined,
    groupByLlmClassifierId: undefined,
    groupByExtendedMetadataKey: undefined,
});

/**
 * The draft a freshly picked option starts from -- empty, because one dimension's values mean
 * nothing to another.
 *
 * `context` carries the few things that are worth keeping across a switch: the classifier the user
 * already picked, the Extended Metadata field the menu entry stands for, and the catalog's default
 * cut points. Each is applied only to the option it belongs to, so nothing leaks between kinds.
 */
export const createGroupByDraft = (
    option: GroupByOptionValue,
    context?: {
        llmClassifierId?: string;
        extendedMetadataKey?: string;
        defaultCutPoints?: number[] | null;
    },
): GroupByDraft => {
    // Question Result starts with no module and no question. Unlike the classifier a Model Results
    // draft can inherit, there is nothing on the chart that reliably names the right question:
    // Question Response Count has one, but grouping by that same question would put one dimension
    // on both axes, so defaulting to it would be actively wrong.
    if (isDateGroupByOption(option)) {
        return { kind: "date", option, grain: "auto" };
    }

    if (isNumericRangeGroupByOption(option)) {
        return {
            kind: "numericRange",
            option,
            cutPoints: [...(context?.defaultCutPoints ?? [])],
        };
    }

    return {
        kind: "categorical",
        option,
        valueIds: [],
        llmClassifierId:
            option === "ModelResults" ? context?.llmClassifierId : undefined,
        extendedMetadataKey:
            option === "ExtendedMetadata"
                ? context?.extendedMetadataKey
                : undefined,
    };
};

/**
 * Ordered so the user is told about a missing prerequisite before anything about the values they
 * cannot meaningfully pick yet.
 */
const validateCategoricalDraft = (
    draft: Extract<GroupByDraft, { kind: "categorical" }>,
): GroupByDraftValidation => {
    if (draft.option === "ModelResults" && !draft.llmClassifierId) {
        return {
            canSave: false,
            message: "Select a model before choosing model results.",
        };
    }

    if (draft.option === "ExtendedMetadata" && !draft.extendedMetadataKey) {
        return {
            canSave: false,
            message:
                "Select an extended metadata field before choosing values.",
        };
    }

    // Two prerequisites, reported in the order the editor asks for them, so the user is never told
    // to pick a question while the module select above it is still empty.
    if (draft.option === "QuestionResult") {
        if (!draft.licensedModuleId) {
            return {
                canSave: false,
                message: "Select a module before choosing question results.",
            };
        }

        if (!draft.questionId) {
            return {
                canSave: false,
                message: "Select a question before choosing question results.",
            };
        }
    }

    if (draft.valueIds.length > MAX_GROUP_BY_SELECTIONS) {
        return { canSave: false, message: groupBySelectionLimitMessage };
    }

    // An option with nothing selected yet is an ordinary in-progress state, so it blocks Save
    // without an error message.
    return { canSave: draft.valueIds.length > 0 };
};

/**
 * How many groups a range may be divided into, and so how many boundaries it may carry: n
 * boundaries make n+1 groups, the last one unbounded. A readability limit like
 * `MAX_GROUP_BY_SELECTIONS`, gating the editor rather than the contract -- the backend caps nothing,
 * and a longer range reaching the API another way still renders.
 */
export const MAX_NUMERIC_RANGE_GROUPS = 10;

export const MAX_NUMERIC_RANGE_CUT_POINTS = MAX_NUMERIC_RANGE_GROUPS - 1;

export const numericRangeGroupLimitMessage = `Max of ${MAX_NUMERIC_RANGE_GROUPS} groups allowed.`;

/**
 * What a range dimension's own filter can express, as the catalog publishes it.
 *
 * A range-grouped chart is capped at the top of its own filter's scale so a bar holds exactly what
 * drilling into it returns. A boundary at or above that cap describes a bar the cap has already
 * emptied, so the resolver rejects it. Passing the same numbers here applies the rule while the user
 * is still typing, without the frontend keeping its own copy of them.
 */
export type NumericRangeLimits = {
    cutPointCeiling?: number | null;
    unit?: string | null;
};

export const cutPointCeilingMessage = (ceiling: number, unit: string) =>
    `Ranges must stay under ${ceiling} ${unit}.`;

/**
 * Why the ranges cannot be saved, in the order the user can act on it.
 *
 * A boundary the user has not typed yet blocks Save without an error: an unfilled row is an ordinary
 * in-progress state, the same as a categorical dimension with nothing selected. Boundaries that do
 * not increase are different -- the user has entered something contradictory, so they are told what
 * is wrong. The message is the one the editor shows beside the offending row.
 */
const validateNumericRangeDraft = (
    draft: NumericRangeDraft,
    limits: NumericRangeLimits | undefined,
): GroupByDraftValidation => {
    // Reported before anything about the groups, so the user is never told to fix a boundary while
    // the module select above it is still empty. A score belongs to one module's evaluation, so
    // without the module there is nothing for the groups to be groups of.
    if (draft.option === "ModuleScore" && !draft.moduleId) {
        return {
            canSave: false,
            message: "Select a module before defining score groups.",
        };
    }

    if (draft.cutPoints.length === 0 || draft.cutPoints.some(isPendingCutPoint)) {
        return { canSave: false };
    }

    if (draft.cutPoints.length > MAX_NUMERIC_RANGE_CUT_POINTS) {
        return { canSave: false, message: numericRangeGroupLimitMessage };
    }

    const ceiling = limits?.cutPointCeiling;
    if (
        ceiling != null &&
        (draft.cutPoints as number[]).some((cutPoint) => cutPoint >= ceiling)
    ) {
        return {
            canSave: false,
            message: cutPointCeilingMessage(ceiling, limits?.unit ?? ""),
        };
    }

    // Boundaries are the only thing the user types, and each ends one row and starts the next, so
    // one that does not exceed the one above it describes a row that ends before it starts. The
    // editor's rows are contiguous by construction otherwise.
    //
    // This is also the whole of a discrete range's extra requirement. Its rows do not share a
    // boundary, so they need theirs a full unit apart rather than merely increasing, which whole
    // numbers that strictly increase already are, and the editor only accepts digits.
    //
    // Zero leads the sequence because the first row's lower bound is zero rather than a boundary the
    // user typed. Without it a first boundary of 0 would pass here and be rejected by the backend,
    // which rejects a non-positive boundary for the same reason: nothing can fall in that row.
    return isStrictlyIncreasing([0, ...(draft.cutPoints as number[])])
        ? { canSave: true }
        : { canSave: false, message: rangesMustBeContinuousMessage };
};

export const rangesMustBeContinuousMessage = "Ranges must be continuous.";

/** Whether a boundary is still waiting to be typed. */
export const isPendingCutPoint = (
    cutPoint: number | null,
): cutPoint is null => cutPoint === null;

/**
 * Whether the draft is complete enough to persist, and what to tell the user when it is not.
 *
 * This is what keeps a draft from being saved in a state the strict reader would later discard,
 * which would leave a chart that looks configured in the drawer but renders ungrouped. An absent
 * draft is valid: a chart is allowed to have no grouping.
 *
 * `limits` are the option's catalog entry, needed only by numeric ranges. Omitting them skips the
 * ceiling check, which is right for a dimension whose filter has no top.
 */
export const validateGroupByDraft = (
    draft: GroupByDraft | undefined,
    limits?: NumericRangeLimits,
): GroupByDraftValidation => {
    if (!draft) return { canSave: true };

    switch (draft.kind) {
        case "categorical":
            return validateCategoricalDraft(draft);
        case "date":
            return { canSave: isDateGroupByGrain(draft.grain) };
        case "numericRange":
            return validateNumericRangeDraft(draft, limits);
        default:
            // Mirrors normalizeCanonicalConfiguration's default case: a kind this function does not
            // yet know about must block Save, not silently pass by falling through to `undefined`.
            return { canSave: false };
    }
};
